import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  VStack,
  Text,
  Link,
  Card,
  Icon,
  Button,
  ButtonGroup,
  DrawerFooter,
} from '@chakra-ui/react';
import {
  mdiGiftOutline,
  mdiButtonPointer,
  mdiSwapHorizontal,
  mdiBookOpenPageVariantOutline,
  mdiAlarmCheck,
} from '@mdi/js';
import {
  Media,
  MediaFigure,
  MediaBody,
  MediaTitle,
} from 'features/common/components/MediaObject';
import { tenantsFallbackIcon } from 'features/common/utils/icons';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { iconByProduct } from 'features/subscriptions/utils';
import { useEffect, useRef, useState } from 'react';
import { ConnectTermsModal } from '../term-modals';
import { useAuth0TokenClaims } from 'features/common/auth/claims';
import { TokenCustomClaimKeysEnum } from '@sitecore-ui/portal-singular';
import { regionCodeToDisplayNameMap } from '../utils/utils';

const sellingPoints: { icon: string; text: string }[] = [
  {
    icon: mdiGiftOutline,
    text: 'No cost for non-production use',
  },
  {
    icon: mdiAlarmCheck,
    text: 'Includes 5,000 tasks per year to explore and build automations',
  },
  {
    icon: mdiBookOpenPageVariantOutline,
    text: 'Full access to all connections, recipe building, and core integration features',
  },
  {
    icon: mdiButtonPointer,
    text: 'Intuitive drag-and-drop interface',
  },
  {
    icon: mdiSwapHorizontal,
    text: 'Flexible data handling using triggers, schedules, and webhooks',
  },
];

export const TryConnectDrawer = () => {
  const connect = iconByProduct.connect;
  const initialFocusRef = useRef(null);
  const [isTermsOpen, setTermsOpen] = useState(false);
  const [{ tryConnectDrawerVisible }, drawerDispatch] =
    useDrawerStateDispatch();
  const { claims } = useAuth0TokenClaims();
  const regionCode = claims?.[TokenCustomClaimKeysEnum.ORG_REGION] || '';
  const region = regionCodeToDisplayNameMap.get(regionCode) || regionCode;

  const ref = useRef<HTMLElement>(document.body);
  useEffect(() => {
    ref.current.style.overflow = 'hidden';
    ref.current.style.touchAction = 'none';
  }, []);

  return (
    <>
      <ConnectTermsModal
        isOpen={isTermsOpen}
        onCloseHandler={setTermsOpen}
      />
      <Drawer
        onClose={() => drawerDispatch(closeDrawer())}
        isOpen={tryConnectDrawerVisible}
        initialFocusRef={initialFocusRef}
        blockScrollOnMount={false}
        size='lg'
        aria-label={
          tryConnectDrawerVisible ? 'trial drawer open' : 'trial drawer closed'
        }
      >
        <DrawerOverlay />
        <DrawerContent aria-label='trial drawer'>
          <HStack pt={7} pb={6}>
            <DrawerCloseButton />
          </HStack>
          <DrawerBody>
            <VStack align={'stretch'} pb={10} gap='8'>
              <VStack align={'start'} gap='8'>
                <Media
                  p={0}
                  as={'div'}
                  size={'2xlarge'}
                  _hover={{
                    backgroundColor: 'transparent',
                    cursor: 'initial',
                  }}
                  _focus={{
                    backgroundColor: 'transparent',
                    cursor: 'initial',
                  }}
                >
                  <MediaFigure
                    icon={{ path: tenantsFallbackIcon }}
                    image={{
                      url: connect.iconUrl,
                      alt: '',
                    }}
                    color={'transparent'}
                  />
                  <MediaBody
                    alignSelf={'center'}
                    display={'flex'}
                    flexDirection={'column'}
                  >
                    <MediaTitle as={'div'}>
                      <HStack>
                        <Heading fontSize='2xl'>{connect.name}</Heading>
                      </HStack>
                    </MediaTitle>
                  </MediaBody>
                </Media>
              </VStack>
              <Card variant='outline' size='md' p='4'>
                <VStack align='flex-start' gap='4'>
                  <Text>
                    The Connect Trial is currently available to all customers.
                    Please review and accept the Terms and Conditions to enable
                    it.
                  </Text>
                  <Text>
                    Get started with integrating Sitecore products and thousands
                    of third-party applications at no cost during your trial.
                  </Text>
                  <Link
                    data-testid='trial-connect-docs-link'
                    variant='ghost'
                    href='https://doc.sitecore.com/connect/en/users/sitecore-connect/introduction-to-sitecore-connect.html'
                    target='_blank'
                  >
                    Read documentation
                  </Link>
                </VStack>
              </Card>
              <VStack
                data-testid='trial-connect-selling-points'
                align='flex-start'
              >
                {sellingPoints.map((point, key) => (
                  <HStack width='full' key={key} gap='4' p='2'>
                    <Icon
                      layerStyle='icon.subtle'
                      boxSize='icon.lg'
                      color='red'
                    >
                      <path d={point.icon} />
                    </Icon>
                    <Text>{point.text}</Text>
                  </HStack>
                ))}
              </VStack>
              {region && (
                <Text
                  data-testid='connect-trial-region'
                  fontSize='md'
                >
                  <Text as='span' fontWeight='semibold'>
                    Deployment region:
                  </Text>{' '}
                  <Text as='span' variant='subtle'>
                    {region}
                  </Text>
                </Text>
              )}
            </VStack>
          </DrawerBody>
          <DrawerFooter justifyContent='flex-end'>
            <ButtonGroup>
              <Button
                data-testid='cancel-btn'
                type='button'
                variant='ghost'
                onClick={() => drawerDispatch(closeDrawer())}
                aria-label='cancel trial'
              >
                Cancel
              </Button>
              <Button
                data-testid='submit-btn'
                type='submit'
                variant='solid'
                aria-label='enable trial'
                onClick={() => setTermsOpen(true)}
              >
                Continue
              </Button>
            </ButtonGroup>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
